/**
 * Global keyboard grammar: chords (`mod+k`), sequences (`g h`, Linear-style "go to") and single keys
 * (`?`, `/`). Pure matcher so the timing and editable-target rules are unit-tested.
 */

export interface HotkeyBinding {
  /** `mod+k`, `shift+?`, `g h`, `?`, `/`, `[` … `mod` is ⌘ on Apple platforms and Ctrl elsewhere. */
  keys: string
  /** Also fire while typing in a text field (only sensible for chords like mod+k). */
  allowInInput?: boolean
}

export interface KeyLike {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
}

interface Step {
  key: string
  mod: boolean
  alt: boolean
  shift: boolean | null // null = don't care (for symbols produced with shift, like `?`)
}

export function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  const p = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? ''
  return /mac|iphone|ipad|ipod/i.test(p)
}

function parseStep(raw: string): Step {
  const parts = raw.toLowerCase().split('+')
  const key = parts.pop() ?? ''
  const mods = new Set(parts)
  const symbol = key.length === 1 && !/[a-z0-9]/.test(key)
  return {
    key,
    mod: mods.has('mod') || mods.has('cmd') || mods.has('ctrl'),
    alt: mods.has('alt'),
    shift: mods.has('shift') ? true : symbol ? null : false,
  }
}

export function parseKeys(keys: string): Step[] {
  return keys.trim().split(/\s+/).map(parseStep)
}

function stepMatches(step: Step, e: KeyLike, apple: boolean): boolean {
  const mod = apple ? e.metaKey : e.ctrlKey
  const otherMod = apple ? e.ctrlKey : e.metaKey
  if (step.mod !== mod || otherMod) return false
  if (step.alt !== e.altKey) return false
  if (step.shift !== null && step.shift !== e.shiftKey) return false
  return e.key.toLowerCase() === step.key
}

export interface SequenceMatcher<B extends HotkeyBinding> {
  /** Feed a keydown; returns the binding it completes, or null. */
  feed(e: KeyLike, now: number, inEditable: boolean): B | null
  /** True while a sequence prefix (e.g. `g`) is waiting for its next key. */
  pending(now: number): boolean
  reset(): void
}

export const SEQUENCE_TIMEOUT_MS = 1200

export function createSequenceMatcher<B extends HotkeyBinding>(bindings: readonly B[], apple = isApple()): SequenceMatcher<B> {
  const parsed = bindings.map((b) => ({ b, steps: parseKeys(b.keys) }))
  let buffer: KeyLike[] = []
  let last = 0
  return {
    feed(e, now, inEditable) {
      if (['Shift', 'Meta', 'Control', 'Alt'].includes(e.key)) return null
      if (now - last > SEQUENCE_TIMEOUT_MS) buffer = []
      last = now
      buffer = [...buffer, e].slice(-3)
      // Longest sequences first, so `g h` wins over a lone `h`.
      for (let len = buffer.length; len >= 1; len--) {
        const tail = buffer.slice(-len)
        for (const { b, steps } of parsed) {
          if (steps.length !== len) continue
          if (inEditable && !b.allowInInput) continue
          if (steps.every((s, i) => stepMatches(s, tail[i]!, apple))) {
            buffer = []
            return b
          }
        }
      }
      // Keep the buffer only if it is a prefix of some sequence.
      const isPrefix = parsed.some(({ steps }) =>
        steps.length > buffer.length && buffer.every((k, i) => stepMatches(steps[i]!, k, apple)),
      )
      if (!isPrefix) buffer = []
      return null
    },
    pending(now) {
      return buffer.length > 0 && now - last <= SEQUENCE_TIMEOUT_MS
    },
    reset() {
      buffer = []
    },
  }
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false
  const el = target as HTMLElement
  if (el.isContentEditable) return true
  const tag = el.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color'].includes(type)
  }
  return false
}

/** Display form: `mod+k` → `⌘K` / `Ctrl K`; `g h` → `G then H` parts. */
export function displayKeys(keys: string, apple = isApple()): string[][] {
  return keys.trim().split(/\s+/).map((step) =>
    step.split('+').map((k) => {
      const l = k.toLowerCase()
      if (l === 'mod') return apple ? '⌘' : 'Ctrl'
      if (l === 'shift') return apple ? '⇧' : 'Shift'
      if (l === 'alt') return apple ? '⌥' : 'Alt'
      if (l === 'enter') return '↵'
      if (l === 'escape' || l === 'esc') return 'Esc'
      if (l === 'arrowup') return '↑'
      if (l === 'arrowdown') return '↓'
      if (l === 'arrowleft') return '←'
      if (l === 'arrowright') return '→'
      return k.length === 1 ? k.toUpperCase() : k
    }),
  )
}
